import React, { useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import services, {
  PaymentTestInfo,
  PaymentTestMode,
  PaymentTestReport,
} from "./estimatePaymentTester.services";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";

type RunButton = {
  mode: PaymentTestMode;
  label: string;
  hint: string;
  destructive?: boolean;
  colorScheme?: string;
};

const RUN_BUTTONS: RunButton[] = [
  {
    mode: "safe",
    label: "Run all safe cases",
    hint: "About 10 seconds. Changes nothing permanently: it puts everything back. Run it as often as you like.",
    colorScheme: "blue",
  },
  {
    mode: "paid",
    label: "Full flow: paid → confirmed",
    hint: "Creates the link, pays it, and confirms the estimate. Uses up the estimate.",
    destructive: true,
    colorScheme: "green",
  },
  {
    mode: "proceed",
    label: "Full flow: Proceed Anyway",
    hint: "Creates the link and confirms through the credit rule. Uses up the estimate.",
    destructive: true,
    colorScheme: "green",
  },
  {
    mode: "settle",
    label: "Customer paid (link made in the app)",
    hint: "After you tapped Confirm in the app: marks that link paid and shows whether the order confirmed.",
    colorScheme: "purple",
  },
  {
    mode: "reset",
    label: "Reset test data",
    hint: "Removes the payment link so the estimate can be tested again. Not possible once confirmed.",
    colorScheme: "orange",
  },
];

const modeTitle = (mode: PaymentTestMode) =>
  RUN_BUTTONS.find((b) => b.mode === mode)?.label ?? mode;

const EstimatePaymentTester: React.FC = () => {
  const [estimateNo, setEstimateNo] = useState("");
  const [info, setInfo] = useState<PaymentTestInfo | null>(null);
  const [report, setReport] = useState<PaymentTestReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<PaymentTestMode | "info" | null>(null);
  const [understood, setUnderstood] = useState(false);

  const trimmed = estimateNo.trim();

  const loadInfo = async (no = trimmed) => {
    if (!no) return;
    setBusy("info");
    setError(null);
    try {
      setInfo(await services.getInfo(no));
    } catch (e) {
      setInfo(null);
      setError(getApiErrorMessage(e, "Could not load the estimate."));
    } finally {
      setBusy(null);
    }
  };

  const run = async (mode: PaymentTestMode) => {
    setBusy(mode);
    setError(null);
    setReport(null);
    try {
      setReport(await services.run(trimmed, mode));
    } catch (e) {
      setError(getApiErrorMessage(e, "The test could not run."));
    } finally {
      setBusy(null);
      await loadInfo(trimmed);
    }
  };

  const grouped = useMemo(() => {
    const map = new Map<string, PaymentTestReport["checks"]>();
    (report?.checks ?? []).forEach((c) => {
      map.set(c.section, [...(map.get(c.section) ?? []), c]);
    });
    return Array.from(map.entries());
  }, [report]);

  return (
    <Box p={4} maxW="900px">
      <VStack align="stretch" spacing={4}>
        <Box>
          <Text fontSize="lg" fontWeight="700">
            Estimate payment tester
          </Text>
          <Text fontSize="sm" color="gray.600">
            Enter the number of a shared estimate of a <b>test customer</b>. The cases run on the
            server against the real payment code, so no tokens or API calls are needed. Payment
            alerts caused by a run are shown here instead of going to Slack.
          </Text>
        </Box>

        <HStack>
          <Input
            aria-label="Estimate number"
            placeholder="Estimate number"
            value={estimateNo}
            onChange={(e) => setEstimateNo(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadInfo()}
            maxW="320px"
          />
          <Button
            colorScheme="blue"
            variant="outline"
            onClick={() => loadInfo()}
            isDisabled={!trimmed || busy !== null}
            isLoading={busy === "info"}
          >
            Check estimate
          </Button>
        </HStack>

        {error && (
          <Alert status="error" borderRadius="md" role="alert">
            <AlertIcon />
            <AlertDescription whiteSpace="pre-wrap">{error}</AlertDescription>
          </Alert>
        )}

        {info && (
          <Box borderWidth="1px" borderRadius="md" p={3} bg="white">
            <Wrap spacing={4} fontSize="sm">
              <WrapItem>Customer: <b>&nbsp;{info.customerCode ?? "?"}</b></WrapItem>
              <WrapItem>
                <Badge colorScheme={info.isTestCustomer ? "green" : "red"}>
                  {info.isTestCustomer ? "test customer" : "not a test customer"}
                </Badge>
              </WrapItem>
              <WrapItem>Enquiry: <b>&nbsp;{info.enquiryNo}</b></WrapItem>
              <WrapItem>Ready splits: <b>&nbsp;{info.readySplits}</b></WrapItem>
              <WrapItem>CBO split: <b>&nbsp;{info.hasCbo ? "yes" : "no"}</b></WrapItem>
              <WrapItem>
                Expires: <b>&nbsp;{info.expiresAt ? new Date(info.expiresAt).toLocaleString() : "?"}</b>
                {info.expired && <Badge ml={2} colorScheme="red">expired</Badge>}
              </WrapItem>
              <WrapItem>
                Payment link:{" "}
                <b>
                  &nbsp;
                  {info.link
                    ? `${info.link.status}${info.link.amount ? ` ₹${info.link.amount}` : ""}${
                        info.link.confirmed ? " · confirmed" : ""
                      }`
                    : "none"}
                </b>
              </WrapItem>
            </Wrap>
            {info.problems.length > 0 && (
              <VStack align="stretch" mt={3} spacing={1}>
                {info.problems.map((p) => (
                  <Text key={p} fontSize="sm" color="red.600">
                    • {p}
                  </Text>
                ))}
              </VStack>
            )}
          </Box>
        )}

        {info && (
          <VStack align="stretch" spacing={3}>
            <Checkbox
              isChecked={understood}
              onChange={(e) => setUnderstood(e.target.checked)}
              size="sm"
            >
              I understand the two "Full flow" buttons use up this estimate (its splits become confirmed).
            </Checkbox>
            {RUN_BUTTONS.map((b) => {
              const allowed = info.runnable[b.mode];
              const needsAck = b.destructive && !understood;
              return (
                <HStack key={b.mode} align="start" spacing={3}>
                  <Button
                    colorScheme={b.colorScheme}
                    minW="270px"
                    onClick={() => run(b.mode)}
                    isDisabled={!allowed || needsAck || busy !== null}
                    isLoading={busy === b.mode}
                    loadingText="Running…"
                  >
                    {b.label}
                  </Button>
                  <Text fontSize="xs" color="gray.600" pt={2}>
                    {b.hint}
                  </Text>
                </HStack>
              );
            })}
          </VStack>
        )}

        {busy && busy !== "info" && (
          <HStack color="gray.600">
            <Spinner size="sm" />
            <Text fontSize="sm">Running {modeTitle(busy as PaymentTestMode)}… this can take up to 20 seconds.</Text>
          </HStack>
        )}

        {report && (
          <Box borderWidth="1px" borderRadius="md" bg="white" p={3} data-testid="report">
            <HStack justify="space-between" mb={2}>
              <Text fontWeight="700">{modeTitle(report.mode)}</Text>
              <HStack>
                <Badge colorScheme={report.failed ? "red" : "green"} fontSize="sm" px={2}>
                  {report.passed}/{report.checks.length} passed
                </Badge>
                <Text fontSize="xs" color="gray.500">
                  {(report.durationMs / 1000).toFixed(1)}s
                </Text>
              </HStack>
            </HStack>

            {grouped.map(([section, items]) => (
              <Box key={section} mt={3}>
                <Text fontSize="sm" fontWeight="600" mb={1}>
                  {section}
                </Text>
                <VStack align="stretch" spacing={1}>
                  {items.map((c, i) => (
                    <HStack key={`${section}-${i}`} align="start" spacing={2} fontSize="sm">
                      <Box pt="3px" color={c.ok ? "green.500" : "red.500"}>
                        {c.ok ? <FaCheckCircle aria-label="passed" /> : <FaTimesCircle aria-label="failed" />}
                      </Box>
                      <Box>
                        <Text>{c.name}</Text>
                        {c.detail && (
                          <Text fontSize="xs" color="gray.500">
                            {c.detail}
                          </Text>
                        )}
                      </Box>
                    </HStack>
                  ))}
                </VStack>
              </Box>
            ))}

            {report.alerts.length > 0 && (
              <>
                <Divider my={3} />
                <Text fontSize="sm" fontWeight="600">
                  Slack alerts this run would have sent (captured, not sent)
                </Text>
                {report.alerts.map((a, i) => (
                  <Text key={i} fontSize="xs" color="gray.600">
                    • {a}
                  </Text>
                ))}
              </>
            )}

            {report.notes.length > 0 && (
              <>
                <Divider my={3} />
                {report.notes.map((n, i) => (
                  <Text key={i} fontSize="xs" color="gray.600">
                    {n}
                  </Text>
                ))}
              </>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default EstimatePaymentTester;
